// @flow
import * as React from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { SidebarItemsModel } from '@jaam/web-core';
import { useCallback, useMemo } from 'react';

type Props = {
  item: SidebarItemsModel;
  className?: string;
};

const isActiveClass = 'xl:!bg-blue-95 rounded-lg text-blue-00';
const defaultClass = 'xl:bg-transparent text-neutral-10';
const textClass = clsx(
  'text-xs font-[400] leading-[16px] tracking-[0.5px] xl:pl-[24px] xl:text-base text-left',
  'max-lg:text-blue-00',
  'text-neutral-10',
);

export const SidebarItem = React.memo(({ item, className }: Props) => {
  const { t } = useTranslation();

  const containerClass = useMemo(
    () =>
      clsx(
        'w-full flex justify-center flex-col items-center rounded-lg outline-none cursor-pointer duration-300 bg-transparent border-none sm:w-full p-[18px] xl:py-2 xl:pr-2 xl:pl-[18px] xl:m-2 xl:flex xl:flex-row xl:items-center xl:justify-start xl:hover:!bg-blue-95 text-inherit decoration-inherit whitespace-nowrap ',
        className,
      ),
    [className],
  );

  const getNavLinkClass = useCallback(
    ({ isActive }: { isActive: boolean }) =>
      clsx(containerClass, isActive ? isActiveClass : defaultClass),
    [containerClass],
  );

  return (
    <NavLink
      to={item.path}
      className={getNavLinkClass}
      aria-label={t(`sidebar.links.${item.code}`)}
    >
      {({ isActive }) => (
        <>
          {/* Render Icon or SVG */}
          {item.icon ? (
            <div className="">
              <item.icon /> {/* Render the icon as a React component */}
            </div>
          ) : (
            <div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={item.svgWidth}
                height={item.svgHeight}
                viewBox={item.svgViewbox}
                fill="none"
                aria-hidden="true"
              >
                <path d={item.svgPath} fill="currentColor" />
              </svg>
            </div>
          )}

          {/* Text */}
          <span className={textClass}>{t(`sidebar.links.${item.code}`)}</span>
        </>
      )}
    </NavLink>
  );
});
